import { Alert, type ComboboxItem, MultiSelect, Stack } from "@mantine/core";
import type { DateTime } from "luxon";
import { useEffect, useMemo } from "react";
import { Userpilot } from "userpilot";
import { useGeneratorSingleEndpointList } from "../../../../hooks/useGeneratorSingleEndpointList";
import { AlertPresets } from "../../../../mantine/mantinePresets";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { sortBackendDates } from "../../../../utils/dates/sortBackendDates";
import { EnergyDataQuality } from "../../../EnergyData/EnergyDataQuality/EnergyDataQuality";
import { LoadOrError } from "../../../LoadOrError/LoadOrError";
import { useEnergyRevenuesPageParams } from "../../hooks/useEnergyRevenuesPageParams";
import { useGeneratorEnergyRevenuesData } from "../../hooks/useGeneratorEnergyRevenuesData";
import { EnergyRevenuesView } from "../EnergyRevenuesView";
import { averageAvailablePercent } from "../utils/energyDataQuality/averageAvailablePercent";
import { calculateTotalVolume } from "../utils/energyDataQuality/calculateTotalVolume";
import { flattenMissingDataSources } from "../utils/energyDataQuality/flattenMissingDataSources";

interface GeneratorSectionProps {
  companyId: number;
  companyLabel: string;
  selectedDateRange: [DateTime<true>, DateTime<true> | null];
  isLoading?: boolean;
}

function GeneratorSection({
  companyId,
  companyLabel,
  selectedDateRange,
  isLoading
}: GeneratorSectionProps) {
  const { generatorIds, setGeneratorIds } = useEnergyRevenuesPageParams();
  const {
    data: generators,
    error: generatorsError,
    isLoading: isGeneratorsLoading
  } = useGeneratorSingleEndpointList({
    vermarktungsvertragCompanyId: companyId
  });

  const generatorOptions = useMemo(
    () =>
      generators?.map<ComboboxItem>((generator) => ({
        label: generator.site_name
          ? `${generator.name} (${generator.site_name})`
          : generator.name,
        value: generator.id.toString()
      })),
    [generators]
  );

  useEffect(() => {
    // If the selected generators(from localstorage) are not in the generator options, show the first generator
    if (
      !isGeneratorsLoading &&
      generatorOptions?.length &&
      generatorIds?.some(
        (generatorId) =>
          !generatorOptions?.some((option) => option.value === generatorId)
      )
    ) {
      setGeneratorIds([generatorOptions[0].value]);
    }
  }, [generatorOptions, generatorIds, setGeneratorIds, isGeneratorsLoading]);

  const selectedGeneratorsLabel = useMemo(() => {
    if (!generatorIds) {
      return "";
    }

    return generatorIds
      .map((generatorId) => {
        const generator = generators?.find(
          (generator) => generator.id === Number(generatorId)
        );

        return generator ? `${generator.name}` : "";
      })
      .join(", ");
  }, [generators, generatorIds]);

  const {
    data: generatorEnergyRevenuesData,
    isLoading: isGeneratorEnergyRevenuesDataLoading,
    error: generatorEnergyRevenuesDataError
  } = useGeneratorEnergyRevenuesData(
    generatorIds?.map((generatorId) => Number(generatorId)) ?? []
  );

  const formattedGeneratorData = useMemo(() => {
    return [...(generatorEnergyRevenuesData ?? [])]
      .filter((energyRevenues) => {
        const revenuesDate = backendDateOrDateTimeToLuxonDateTime(
          energyRevenues.period_start
        );

        return (
          selectedDateRange[0] <= revenuesDate &&
          (selectedDateRange[1] === null ||
            revenuesDate <= selectedDateRange[1])
        );
      })
      .sort((revenuesA, revenuesB) =>
        sortBackendDates(revenuesA.period_start, revenuesB.period_start)
      );
  }, [generatorEnergyRevenuesData, selectedDateRange]);

  return (
    <Stack gap="sm">
      <MultiSelect
        className="generator-select"
        data={generatorOptions}
        disabled={isGeneratorsLoading || generators?.length === 0}
        label="Anlagen"
        placeholder="Anlagen auswählen"
        size="xs"
        value={generatorIds}
        onChange={(newGeneratorIds) => {
          setGeneratorIds(newGeneratorIds);
          Userpilot.track("em_anlagenansicht_anlagen_feld");
        }}
      />

      {generatorIds && generatorIds.length === 0 ? (
        <Alert color={AlertPresets.info.color} icon={AlertPresets.info.icon}>
          Bitte wählen Sie mindestens eine Anlage aus, um die Erlösdaten
          anzuzeigen.
        </Alert>
      ) : (
        <EnergyDataQuality
          isLoading={
            isLoading ||
            isGeneratorsLoading ||
            isGeneratorEnergyRevenuesDataLoading
          }
          missingDataSources={flattenMissingDataSources(formattedGeneratorData)}
          qualityPercent={averageAvailablePercent(formattedGeneratorData)}
          selectedDateRange={selectedDateRange}
          totalVolume={calculateTotalVolume(formattedGeneratorData)}
        />
      )}
      <LoadOrError error={generatorsError || generatorEnergyRevenuesDataError}>
        <EnergyRevenuesView
          energyRevenuesData={formattedGeneratorData}
          exportFileName={`${companyLabel} - ${selectedGeneratorsLabel}`}
          isLoading={
            isLoading ||
            isGeneratorsLoading ||
            isGeneratorEnergyRevenuesDataLoading
          }
        />
      </LoadOrError>
    </Stack>
  );
}

export { GeneratorSection };
